import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { RootState } from "..";
import { DocumentFilterInput, DocumentFilterTypeEnum, DocumentSortFieldEnum, DocumentSortInput, DocumentSortOrderEnum } from "@/app/graphql/codegen";

export type DocumentSearchTerm = DocumentFilterInput["searchTerm"];
export type DocumentFilterType = DocumentFilterInput["type"];
export type DocumentSortField = DocumentSortInput["field"];
export type DocumentSortOrder = DocumentSortInput["order"];

export interface State {
    listId?: string; // ID des Dokuments, das in der Liste ausgewählt ist
    searchTerm: DocumentSearchTerm;
    type: DocumentFilterType;
    sortField: DocumentSortField;
    sortOrder: DocumentSortOrder;
    selectedDocumentIds: string[]; // Now an array for multiple selections
    jurisdiction: string; // Filter by jurisdiction (e.g. "all", "EU", "GER")
}

const initialState: State = {
    listId: undefined,
    searchTerm: "",
    type: DocumentFilterTypeEnum.All,
    sortField: DocumentSortFieldEnum.Title,
    sortOrder: DocumentSortOrderEnum.Asc,
    selectedDocumentIds: [], // Initialize as empty array
    jurisdiction: "all",
};

export const documentSelectionSlice = createSlice({
    name: "documentSelection",
    initialState,
    reducers: {
        setListSelection: (state, action: PayloadAction<string>) => {
            state.listId = action.payload;
        },
        setSearchTerm: (state, action: PayloadAction<string>) => {
            state.searchTerm = action.payload;
        },
        setTypeFilter: (state, action: PayloadAction<DocumentFilterInput["type"]>) => {
            state.type = action.payload;
        },
        setJurisdictionFilter: (state, action: PayloadAction<string>) => {
            state.jurisdiction = action.payload;
        },
        setSortField: (state, action: PayloadAction<DocumentSortInput["field"]>) => {
            state.sortField = action.payload;
        },
        setSortOrder: (state, action: PayloadAction<DocumentSortInput["order"]>) => {
            state.sortOrder = action.payload;
        },
        toggleDocumentSelection: (state, action: PayloadAction<string>) => {
            const documentId = action.payload;
            const index = state.selectedDocumentIds.indexOf(documentId);
            if (index === -1) {
                state.selectedDocumentIds.push(documentId);
            } else {
                state.selectedDocumentIds.splice(index, 1);
            }
        },
        clearDocumentSelection: (state) => {
            state.selectedDocumentIds = [];
        },
        resetFilters: (state) => {
            return {
                ...initialState,
                selectedDocumentIds: state.selectedDocumentIds, // Keep selections when resetting filters
            };
        },
    },
});

export const { setListSelection, setSearchTerm, setTypeFilter, setSortField, setSortOrder, toggleDocumentSelection, clearDocumentSelection, resetFilters, setJurisdictionFilter } = documentSelectionSlice.actions;

// Selectors
export const useDocumentSelectionSlice = () => useSelector((state: RootState) => state.documentSelection);

export const selectDocumentFilters = createSelector(
    (state: RootState) => state.documentSelection,
    (documentSelection) => ({
        listId: documentSelection.listId,
        searchTerm: documentSelection.searchTerm,
        type: documentSelection.type,
        sortField: documentSelection.sortField,
        sortOrder: documentSelection.sortOrder,
        jurisdiction: documentSelection.jurisdiction,
    }),
);

export const selectSelectedDocumentIds = (state: RootState) => state.documentSelection.selectedDocumentIds;

export default documentSelectionSlice.reducer;
